// Single source of truth for how a pregnancy was conceived, shared by the FHQ pregnancy form
// and the pedigree editor so the two surfaces can never drift (and neither can invent a donor).
//
// The UI captures TWO independent axes:
//   - partner: a spouse reference (FamilyChanged _id / pid), or null for "no partner"
//   - donors:  sperm donor? and/or egg donor? (independent booleans)
// From those, deriveConception produces the persisted record fields (Bennett):
//   conceivedWith ∈ '' | 'partner' | 'spermDonor' | 'eggDonor' | 'embryoDonor' | 'partnerAndDonor'
//   donors[]      = the gamete-donor satellites
//   otherParentMemberId = the in-graph spouse (only when partner-conceived)
//
// PRINCIPLE: nothing defaults to a donor. With no partner and no donor selected, conceivedWith
// is '' (unspecified) — the pregnancy renders carrier-alone. A donor appears ONLY when the user
// explicitly ticks it.
//
// DONOR IDENTITY (TGCLOUD-1597) is a THIRD, OPTIONAL axis: who the donor is. A donor stays
// anonymous unless a member reference is supplied, at which point `anonymous` goes false and
// `memberId` names the person. `memberId` has been declared on the Pregnancy schema since the
// original repro work ("future: a known donor who is also a member") and this is what finally
// writes it.
//
// The FHQ (src/components/questionnaire/pregnancies/PregnanciesDetailsForm.jsx) shares this
// module and must NOT gain donor identity — a patient cannot name a gamete donor. Both identity
// arguments therefore default to null, and with them absent every output field is byte-identical
// to the pre-TGCLOUD-1597 shape. There is a test pinning exactly that.

const mkDonor = (role, memberId) =>
  memberId == null || memberId === ''
    ? { role, anonymous: true }
    : { role, anonymous: false, memberId: String(memberId) };

export function deriveConception({
  spouseRef = null,
  spermDonor = false,
  eggDonor = false,
  spermDonorMemberId = null,
  eggDonorMemberId = null
} = {}) {
  const donors = [];
  if (eggDonor) donors.push(mkDonor('ovum', eggDonorMemberId));
  if (spermDonor) donors.push(mkDonor('sperm', spermDonorMemberId));

  const hasPartner = spouseRef != null && spouseRef !== '';
  const hasDonor = donors.length > 0;

  let conceivedWith = '';
  if (hasPartner && hasDonor) conceivedWith = 'partnerAndDonor';
  else if (hasPartner) conceivedWith = 'partner';
  else if (spermDonor && eggDonor) conceivedWith = 'embryoDonor';
  else if (spermDonor) conceivedWith = 'spermDonor';
  else if (eggDonor) conceivedWith = 'eggDonor';
  // else '' — unspecified, carrier-alone (never assume a donor)

  return {
    conceivedWith,
    donors,
    otherParentMemberId: hasPartner ? spouseRef : undefined
  };
}

// Biological-possibility check, shared by the FHQ form and the pedigree editor. The carrier is
// always biologically female here (only a gestational/female carrier reaches this UI), so she
// contributes the egg; the pregnancy still needs a sperm source. If a partner is explicitly
// NAMED, they must be able to provide it — a biologically-male partner — otherwise a sperm
// donor is mandatory (two biological females need a sperm donor). A fully UNSPECIFIED
// conception (no named partner) is left blank and NOT forced to a donor (never assume).
// Returns an error string to block save, or null.
export function conceptionValidationError({
  partnerSpecified = false,
  partnerGender = null,
  spermDonor = false
} = {}) {
  if (partnerSpecified && partnerGender !== 'male' && !spermDonor) {
    return 'A same-sex (non-male) partner needs a sperm donor — add a sperm donor, or choose a male partner.';
  }
  return null;
}

// DONOR IDENTITY validation (TGCLOUD-1597), extending the TGCLOUD-1535 sane-state rules. Pure and
// shared so the rule lives in one place. Returns an error string to block save, or null.
//
// Blocking, because each of these produces a chart that cannot be read:
//   - a person cannot be their own donor (TGCLOUD-1535 states this rule explicitly), and the
//     carrier gestating the pregnancy cannot be a donor to it — the edge would be a self-loop;
//   - one person cannot be both the sperm and the egg donor for the same pregnancy.
//
// Sex plausibility is deliberately NOT blocked. A donor's recorded sex may be absent or, for a
// trans donor, may not match the gamete; refusing the save would make real families unrecordable.
// donorSexWarning below is the non-blocking counterpart.
export function donorIdentityValidationError({
  carrierRef = null,
  spermDonorMemberId = null,
  eggDonorMemberId = null,
  refsMatch = (a, b) => a != null && b != null && String(a) === String(b)
} = {}) {
  const ids = [spermDonorMemberId, eggDonorMemberId].filter(
    (v) => v != null && v !== ''
  );
  if (ids.some((id) => refsMatch(id, carrierRef))) {
    return 'The person carrying this pregnancy cannot also be its gamete donor.';
  }
  if (ids.length === 2 && refsMatch(spermDonorMemberId, eggDonorMemberId)) {
    return 'The same person cannot be both the sperm donor and the egg donor.';
  }
  return null;
}

// Non-blocking counterpart: the gamete a donor is recorded against does not match their recorded
// sex. Worth saying out loud (it is usually a mis-pick) but never worth refusing — see above.
// `sexOf` maps a member reference to 'male' | 'female' | null.
export function donorSexWarning({
  spermDonorMemberId = null,
  eggDonorMemberId = null,
  sexOf = () => null
} = {}) {
  const notes = [];
  const sperm = spermDonorMemberId ? sexOf(spermDonorMemberId) : null;
  const egg = eggDonorMemberId ? sexOf(eggDonorMemberId) : null;
  if (sperm && sperm !== 'male') {
    notes.push('the sperm donor is not recorded as male');
  }
  if (egg && egg !== 'female') {
    notes.push('the egg donor is not recorded as female');
  }
  return notes.length
    ? `Check the donor selection — ${notes.join(
        ' and '
      )}. This has been saved as entered.`
    : null;
}

// Inverse: recover the UI axes from a stored record, so the editor can seed its controls.
// Handles legacy 'embryoDonor' records that predate the donors[] array (treated as both donors).
export function conceptionToAxes(record = {}) {
  const donors = Array.isArray(record.donors) ? record.donors : [];
  const cw = record.conceivedWith;
  const partnerConceived = cw === 'partner' || cw === 'partnerAndDonor';
  const embryoNoDonors = cw === 'embryoDonor' && donors.length === 0;
  const memberIdOf = (role) => {
    const hit = donors.find((d) => d.role === role && d.memberId != null);
    return hit ? String(hit.memberId) : null;
  };
  return {
    spouseRef: partnerConceived ? record.otherParentMemberId ?? null : null,
    spermDonor: donors.some((d) => d.role === 'sperm') || embryoNoDonors,
    eggDonor: donors.some((d) => d.role === 'ovum') || embryoNoDonors,
    // null for an anonymous donor — which is every donor recorded before TGCLOUD-1597, and every
    // donor the FHQ will ever write.
    spermDonorMemberId: memberIdOf('sperm'),
    eggDonorMemberId: memberIdOf('ovum')
  };
}
